/**
 * ScreenReviewApproved - Tela de Aguardando Aprovação
 * Exibida após o motorista submeter o formulário de onboarding
 * Informa que os dados estão em revisão pelo administrador
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Clock, CheckCircle, FileText, Bell } from 'lucide-react';
import { toast } from 'sonner';

export function ScreenReviewApproved() {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [driverName, setDriverName] = useState<string>('');
    const [profileStatus, setProfileStatus] = useState<string>('pending_review');

    useEffect(() => {
        async function checkStatus() {
            try {
                const { data: { user }, error: authError } = await supabase.auth.getUser();

                if (authError || !user) {
                    navigate('/driver/login', { replace: true });
                    return;
                }

                // Verificar o status do perfil
                const { data: driverData, error: driverError } = await supabase
                    .from('drivers')
                    .select('nome_completo, profile_status')
                    .eq('id', user.id)
                    .single();

                if (driverError) {
                    console.error('[ScreenReviewApproved] Erro ao buscar driver:', driverError);
                    navigate('/driver/login', { replace: true });
                    return;
                }

                setDriverName(driverData.nome_completo || 'Motorista');
                setProfileStatus(driverData.profile_status);

                // Redirecionar baseado no status
                if (driverData.profile_status === 'approved') {
                    navigate('/dash_companion', { replace: true });
                    return;
                }

                if (driverData.profile_status === 'pending_submission') {
                    navigate('/driver/onboarding', { replace: true });
                    return;
                }

                // Novos status: pending_correction e rejected
                if (driverData.profile_status === 'pending_correction') {
                    navigate('/driver/correction', { replace: true });
                    return;
                }

                if (driverData.profile_status === 'rejected') {
                    navigate('/driver/rejected', { replace: true });
                    return;
                }

                setIsLoading(false);
            } catch (error) {
                console.error('[ScreenReviewApproved] Erro:', error);
                navigate('/driver/login', { replace: true });
            }
        }

        checkStatus();
    }, [navigate]);

    // Configurar listener para mudanças em tempo real
    useEffect(() => {
        let subscription: any;

        async function setupRealtimeListener() {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            subscription = supabase
                .channel('driver-review-status-changes')
                .on(
                    'postgres_changes',
                    {
                        event: 'UPDATE',
                        schema: 'public',
                        table: 'drivers',
                        filter: `id=eq.${user.id}`
                    },
                    (payload: any) => {
                        const newStatus = payload.new?.profile_status;
                        console.log('[ScreenReviewApproved] Status alterado para:', newStatus);

                        if (newStatus === 'approved') {
                            toast.success('Parabéns! Seu cadastro foi aprovado!');
                            navigate('/dash_companion', { replace: true });
                        } else if (newStatus === 'pending_correction') {
                            toast.info('Você tem uma notificação sobre seu cadastro');
                            navigate('/driver/correction', { replace: true });
                        } else if (newStatus === 'rejected') {
                            navigate('/driver/rejected', { replace: true });
                        }
                    }
                )
                .subscribe();
        }

        setupRealtimeListener();

        return () => {
            if (subscription) {
                supabase.removeChannel(subscription);
            }
        };
    }, [navigate]);

    // Polling como fallback (verifica a cada 30 segundos)
    useEffect(() => {
        const interval = setInterval(async () => {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;

            const { data: driverData } = await supabase
                .from('drivers')
                .select('profile_status')
                .eq('id', user.id)
                .single();

            if (driverData?.profile_status === 'approved') {
                navigate('/dash_companion', { replace: true });
            }
        }, 30000);

        return () => clearInterval(interval);
    }, [navigate]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50 flex items-center justify-center">
                <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-emerald-50/30 to-gray-50 flex flex-col items-center justify-center p-4">
            {/* Container Principal */}
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <img
                        src="https://locaninja.com/logotipo-watch.png"
                        alt="Watch Logo"
                        className="h-12 mx-auto mb-4"
                    />
                </div>

                {/* Card Principal */}
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
                    {/* Header do Card */}
                    <div className="bg-gradient-to-r from-emerald-600 to-green-700 p-6">
                        <div className="flex items-center justify-center">
                            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                <Clock className="w-8 h-8 text-white" />
                            </div>
                        </div>
                        <h1 className="text-xl font-bold text-white text-center mt-4">
                            Cadastro em Análise
                        </h1>
                    </div>

                    {/* Conteúdo do Card */}
                    <div className="p-6">
                        {/* Saudação */}
                        <p className="text-gray-700 text-center mb-6">
                            Olá, <span className="font-semibold text-gray-900">{driverName}</span>
                        </p>

                        {/* Mensagem Principal */}
                        <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 mb-6">
                            <p className="text-emerald-800 text-sm leading-relaxed text-center">
                                Recebemos suas informações e documentos. Nossa equipe está analisando
                                seu cadastro para garantir a segurança e qualidade da nossa plataforma.
                            </p>
                        </div>

                        {/* Timeline de Status */}
                        <div className="space-y-4 mb-6">
                            {/* Etapa 1 - Concluída */}
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">Cadastro enviado</p>
                                    <p className="text-xs text-gray-500">Suas informações foram recebidas</p>
                                </div>
                            </div>

                            {/* Etapa 2 - Em Andamento */}
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                    <FileText className="w-5 h-5 text-emerald-600 animate-pulse" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">Em análise</p>
                                    <p className="text-xs text-gray-500">Nossa equipe está verificando seus dados</p>
                                </div>
                            </div>

                            {/* Etapa 3 - Pendente */}
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                    <Bell className="w-5 h-5 text-gray-400" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-400">Aprovação</p>
                                    <p className="text-xs text-gray-400">Você será notificado quando aprovado</p>
                                </div>
                            </div>
                        </div>

                        {/* Informação de Contato */}
                        <div className="bg-gray-50 rounded-xl p-4">
                            <p className="text-gray-600 text-xs text-center leading-relaxed">
                                O processo de análise geralmente leva até <strong>48 horas úteis</strong>.
                                Caso tenha dúvidas, entre em contato com nosso suporte.
                            </p>
                        </div>
                    </div>

                    {/* Footer do Card */}
                    <div className="border-t border-gray-100 p-4 bg-gray-50">
                        <button
                            onClick={async () => {
                                await supabase.auth.signOut();
                                navigate('/driver/login', { replace: true });
                            }}
                            className="w-full py-3 text-gray-600 hover:text-gray-800 font-medium text-sm transition-colors"
                        >
                            Sair da conta
                        </button>
                    </div>
                </div>

                {/* Texto de Rodapé */}
                <p className="text-center text-gray-400 text-xs mt-6">
                    Esta página atualiza automaticamente quando seu cadastro for aprovado.
                </p>
            </div>
        </div>
    );
}

export default ScreenReviewApproved;
